import type { BoardEvent, BoardTableState, Envelope } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { showCarpet } from "../game/carpet";
import { BreakWatch, RESULT_EVENTS } from "../game/handBreak";
import { botText } from "../game/seats";
import { Sequencer } from "../scene/queue";
import { wait } from "../scene/fly";
import { notify } from "../ui/Dialog";
import { sfx, synth, type Sfx } from "../game/sfx";
import { ms } from "../game/speed";
import { menchSound } from "./menchSfx";
import { throwVelocity } from "./diceThrow";
import { NARD_FIELD, NCHK, nardCorner, nardLandXY, nardBarXY, nardChkXY, nardSnap, nardTopXY } from "./nardGeom";
import {
  BASE, CAR_TURNS, DICE_CHOOSE, DICE_SUM, GOAL, HOME0, MAGIC_ANTIDOTE, MAGIC_CAR, MAGIC_SWORD, MAIN_LAST, NARD_BAR, NARD_OFF, POWER_DOUBLE, POWER_FA, POWER_SHIELD, boardLocal,
  marpeleCorner, marpeleXY, menchColor, menchPartner, menchPath, menchPickable, menchPowerXY, menchTokenXY, nardServerPoint, toLocal,
  type BoardLocal, type DominoLocal, type DoozLocal, type MarpeleLocal, type MenchLocal, type NardLocal,
} from "./state";

/** صحنه‌ی زدنِ منچ: فاصله‌ی هر یک از دو مهره از وسطِ خانه (خانه) و لحظه‌ی ضربه (کسرِ صحنه) — همان keyframes ِ .mn-bonk. */
export const BONK_DX = 0.45, BONK_IMPACT = 0.6;
/** صحنه‌ی شمشیرِ مارپله: لحظه‌ی خوردنِ تیغه به سرِ مار (کسرِ صحنه) — همان keyframes ِ .mp-strike و SnakeView.StrikeImpact. */
export const STRIKE_IMPACT = 0.6;
/** سقفِ پادزهر و شمشیرِ هر نفر — همان maxPower ِ سرور. */
const MAX_POWER = 2;


/**
 * کنترلرِ میزِ تخته‌ایِ آنلاین — همتای BoardOnlineTable و کنترلرهای آنلاینِ اپ.
 *
 * سرور اول رویداد می‌فرستد، بعد وضعیت؛ هر دو پشتِ هم در صفِ صحنه (Sequencer) اجرا
 * می‌شوند تا وضعیتِ تازه زیرِ انیمیشنِ رویداد ننشیند. انیمیشن‌ها ساده‌اند: مدل
 * گام‌به‌گام جلو می‌رود و CSS جابه‌جایی را نرم می‌کند.
 *
 * حرکتِ فرستاده «در راه» است تا سرور جواب بدهد (هر رویداد، نمای متفاوت یا خطا) —
 * وگرنه نمای میان‌راه (تماشاچیِ تازه، چتِ بسته) دکمه‌ی «تاس بریز» را برمی‌گرداند و لمسِ
 * دوم «نوبتِ تو نیست» می‌گرفت. ششِ بی‌حرکت همان نما را برمی‌گرداند، پس «جواب» فقط
 * «نمای متفاوت» نیست.
 */
export class BoardOnline {
  private seq = new Sequencer();
  private off: (() => void) | null = null;
  private quitting = false;
  private carpetApplied = false;
  private pending = false;
  private sentKey = "";
  private manual = false;
  private last: BoardTableState | null = null;
  private turnEndsAt = 0; private turnTotal = 0;
  private tick: ReturnType<typeof setInterval> | null = null;

  /** مدلِ صفحه؛ null تا رسیدنِ اولین نما. */
  readonly model = signal<BoardLocal | null>(null);
  /** تاس در حالِ چرخیدن. */
  readonly rolling = signal(false);
  /** مهره‌ی دوزی که همین حالا می‌افتد (کلیدِ خانه‌اش) — برای انیمیشنِ افتادن. */
  readonly dropping = signal<string>("");
  /** مهلتِ نوبتِ من (ثانیه)؛ null = نشان نده. */
  readonly timer = signal<{ left: number; total: number } | null>(null);
  /** پایانِ بازی نشان داده شد. */
  readonly ended = signal(false);
  /** دوز: پنلِ پایان تا دیده‌شدنِ خطِ برنده صبر می‌کند (DOOZ_LINE_HOLD) */
  private endHold: ReturnType<typeof setTimeout> | 0 = 0;
  /** تخته‌نرد: خانه‌ی انتخاب‌شده برای حرکت (−۹۹ = هیچ). */
  readonly nardFrom = signal(-99);
  /**
   * تخته‌نرد: مهره‌ی پرنده (واحدِ تخته). مدل گام‌به‌گام جلو می‌رود — مهره اول از مبدأ برداشته
   * می‌شود و بعد از فرود در مقصد است؛ زده‌شده تا پایانِ پروازش در بار نیست — پس هیچ مهره‌ای
   * دو جا دیده نمی‌شود. `off` یعنی نزدیکِ سینی به پهلو می‌خوابد.
   */
  readonly nardFly = signal<{ from: [number, number]; to: [number, number]; seat: number; ms: number; hit: boolean; off: boolean; id: number } | null>(null);
  /** تخته‌نرد: حلقه‌ی باز‌شونده‌ی لحظه‌ی زدن (واحدِ تخته). */
  readonly nardRing = signal<{ x: number; y: number; id: number } | null>(null);
  /**
   * تخته‌نرد: مهره‌ای که با کشیدن روی مقصد رها شد و منتظرِ جوابِ سرور است. همان‌جا کشیده
   * می‌شود (و یکی از مبدأ کم) تا پرواز از همان‌جا شروع شود، نه این‌که اول به مبدأ برگردد.
   */
  readonly nardHeld = signal<{ from: number; seat: number; at: [number, number] } | null>(null);
  /** تخته‌نرد: لمسِ جای بی‌حرکت — نشان‌های مجاز تکان می‌خورند (هر عددِ تازه یک بار). */
  readonly nardNudge = signal(0);
  /** تخته‌نرد: راهنمای گذرا در خطِ وضعیت، تا این لحظه (performance.now). */
  readonly nardHint = signal<{ text: string; until: number } | null>(null);
  /**
   * تخته‌نرد: تاس‌های پرتابی روی تخته — همان ThrownDie ِ منچ و مارپله (کاربر، ۲۷ اکتبر ۲۰۲۶: «تخته را با تغییرهای بازی‌های
   * دیگر جور کن؛ منطق و مهره همان، تاس عوض شود»). هر تاس از گوشه‌ی خودش: نوبتِ عادی دو تاس کنارِ هم از گوشه‌ی ریزنده،
   * تاسِ آغازین یکی از گوشه‌ی هر بازیکن. v به واحدِ زمینِ پرتابِ تخته‌نرد (NARD_FIELD)؛ seats جعبه‌هایی که در طولِ پرتاب
   * خالی‌اند.
   */
  readonly nardThrow = signal<{ seats: number[]; throws: { corner: number; v: [number, number]; dx: number; value: number }[]; ms: number; hold: number; id: number } | null>(null);
  private nardOpening = false;
  /** دومینو: مهره‌ای که به هر دو سر می‌خورد و منتظرِ انتخابِ سر است (−۱ = هیچ). */
  readonly dominoSide = signal(-1);
  /** دومینو: مهره‌ی آخری که نشست (سر: −۱ اول، ۰ چپ، ۱ راست) — طلایی کشیده می‌شود. */
  readonly dominoLast = signal<{ side: number; a: number; b: number } | null>(null);
  /** دومینو: پنلِ پایانِ دور. */
  readonly roundOver = signal(false);
  /** دومینو: مکثِ میانِ دو دور — «ادامه» ِ همه ([[BreakWatch]]). */
  readonly brk = new BreakWatch();
  /** مارپله: جلوه‌ی لحظه — نیش، شمشیر، پادزهر، برداشتنِ جادو (id تا تکرارِ پشتِ هم هم دیده شود). */
  readonly mpFx = signal<{ kind: "bite" | "sword" | "antidote" | "pickup"; cell: number; magic?: number; id: number } | null>(null);
  /** مارپله: نوارِ واکنش برای بقیه، بعد از نیش یا شمشیر (تا این لحظه، performance.now). */
  readonly reactUntil = signal(0);
  /** مارپله: تاسی که از دو تا برداشته شد (برای برجسته کردن). */
  readonly mpPicked = signal(0);
  /**
   * مارپله: تاس(های) پرتابی روی تخته — همان ThrownDie ِ منچ؛ با دو تاس، دومی از کنارِ اولی و کمی کج‌تر.
   * v به واحدِ تخته‌ی منچ (۱۵ خانه) است و نما آن را روی ۱۰ خانه‌ی مارپله می‌کشد.
   */
  readonly mpThrow = signal<{ seat: number; corner: number; throws: { v: [number, number]; dx: number; value: number }[]; ms: number; hold: number; id: number } | null>(null);
  /** مارپله: آدمکِ در حالِ رفتن — گام (پای چپ/راست با زوج و فرد)، پرش از نردبان، ترس و افتادن روی مار. */
  readonly mpWalk = signal<{ seat: number; step: number; pose: "step" | "jump" | "scared" | "fall" } | null>(null);
  /**
   * مارپله: ماشینِ seat سرِ خانه‌ی cell کوبید — ماشین روی چرخِ عقب بلند می‌شود و هر زده‌شده افتاده و چرخان از خانه‌اش تا
   * جایش روی نوارِ «شروع» پرواز می‌کند (مختصاتِ تخته).
   */
  readonly mpRam = signal<{ seat: number; cell: number; victims: { s: number; from: [number, number]; to: [number, number] }[]; id: number } | null>(null);
  /**
   * مارپله، صحنه‌ی شمشیر (کاربر، ۲۶ اکتبر ۲۰۲۶: «وقتی شمشیر را به کار می‌برد، انیمیشنی باشد که با شمشیر به مار می‌زند»):
   * آدمکِ زره‌پوشِ seat کنارِ سرِ مارِ head می‌ایستد، شمشیر را بالا می‌برد و بر سرِ مار فرود می‌آورد. ms کلِ صحنه، ضربه در
   * STRIKE_IMPACT. آدمکِ خودش در این مدت پنهان است.
   */
  readonly mpStrike = signal<{ seat: number; head: number; ms: number; id: number } | null>(null);
  /** مارپله: carhit ها پیش از move ِ همان حرکت می‌آیند — خانه → صندلی‌های محلیِ زده‌شده، تا راه‌رفتن سرِ همان خانه بکوبد. */
  private mpRams = new Map<number, number[]>();
  private mpSteps = 0;
  private fxId = 0;

  /** منچ: جلوه‌ی لحظه روی تخته (واحدِ خانه) — «burst» بیرون‌آمدن از خانه، «sparkle» رسیدنِ مهره‌ی دیگری. */
  readonly mnFx = signal<{ kind: "burst" | "sparkle"; x: number; y: number; color: string; id: number } | null>(null);
  /**
   * منچ: مهره‌ی زده‌شده در پرواز به خانه‌اش (چرخان). مهره‌ی واقعی تا پایانِ پرواز سرِ همان
   * خانه‌ی فرود می‌مانَد و پنهان است — وگرنه زده‌شده اول در خانه‌اش سبز می‌شد و بعد پرواز می‌کرد.
   */
  readonly mnFly = signal<{ seat: number; tok: number; from: [number, number]; to: [number, number]; color: string; ms: number; id: number } | null>(null);
  /** منچ: لرزشِ تخته — هر عددِ تازه یک لرزش (فقط وقتی خودت زدی یا زده شدی). */
  readonly mnShake = signal(0);
  /** منچ: حبابِ روی پلاک — «😱» ِ زده‌شده — تا این لحظه (performance.now). */
  readonly mnBubble = signal<{ seat: number; text: string; until: number; id: number } | null>(null);
  /**
   * آدمک‌های منچ (آرتِ کاربر): مهره‌ای که همین حالا راه می‌رود (هر گام یک جهش با دو ژستِ گامِ یک‌درمیان؛ از
   * خانه با ژستِ پرش)، و ژست‌های گذرا — ترسیده لحظه‌ی زده‌شدن، شادی سرِ رسیدن. کلید: «صندلی-مهره».
   */
  /**
   * صحنه‌ی زدن (کاربر، ۲۵ اکتبر): مهره‌ی زننده (seat، tok) چوبدستی را از پشتش درمی‌آورد و بر سرِ زده‌شده
   * (vseat، vtok) می‌زند — هر دو کنارِ هم روی خانه‌ی (x، y)؛ ci و vci رنگِ آرت. ms کلِ صحنه، ضربه در BONK_IMPACT.
   */
  readonly mnBonk = signal<{ seat: number; tok: number; vseat: number; vtok: number; x: number; y: number; ci: number; vci: number; girl: boolean; vgirl: boolean; ms: number; id: number } | null>(null);
  /**
   * پرتابِ تاسِ منچ روی تخته (diceThrow.ts): تاسِ صندلیِ seat از گوشه‌ی corner با سرعتِ v (خانه بر ثانیه)،
   * در ms میلی‌ثانیه روی value می‌ایستد؛ بعد hold میلی‌ثانیه می‌ماند. تا پایان، تاسِ جعبه‌ی آن صندلی پنهان است.
   */
  readonly mnThrow = signal<{ seat: number; corner: number; v: [number, number]; value: number; ms: number; hold: number; id: number } | null>(null);
  readonly mnWalk = signal<{ seat: number; tok: number; step: number; base: boolean } | null>(null);
  readonly mnPose = signal<{ key: string; pose: "scared" | "cheer"; until: number } | null>(null);
  /** منچ: خطِ وضعیتِ گذرا — «سارا — شانسِ دوم»، «سارا برای یارش بازی می‌کند» — تا این لحظه. */
  readonly mnNote = signal<{ text: string; until: number } | null>(null);
  /** کاغذرنگی (منچ): رتبه‌ی اولِ خودت یا پایانِ برده — هر عددِ تازه یک بار. */
  readonly confetti = signal(0);
  /** منچ: خانه‌ی فرودِ آخرین حرکت — پروازِ زده‌شده از همین‌جا شروع می‌شود. */
  private mnLand: { seat: number; tok: number; to: number } | null = null;
  /** سرعتِ کشیدنِ انگشتِ آخرین «بریز» ِ من — پرتابِ بعدیِ تاسِ خودم همان سو. */
  private mnSwipe: [number, number] | null = null;
  /** شمارِ hit ِ حرکتِ جاری — فقط اولی صحنه‌ی چوبدستی دارد. */
  private mnHits = 0;
  /** منچ: تاسِ آخر و «سه‌تا شش» پیش از pass — برای متنِ «شش بود، دوباره بریز». */
  private mnDie = 0;
  private mnForfeit = false;
  /** منچ: تاس‌ریزِ آخرین حرکت (تیمی: ممکن است یارِ صاحبِ مهره باشد). */
  private mnRoller = -1;
  /**
   * منچ: ریزنده‌ای که همین حالا با ۶ حرکت کرد (−۱ هیچ). اگر نمای بعد همان صندلی را در فازِ تاس نشان داد،
   * «شش! یک تاسِ دیگر» — تاسِ اضافه‌ی شش رویدادی ندارد و بازیکن نمی‌فهمید جایزه رسید (کاربر، ۲۵ اکتبر ۲۰۲۶).
   * ششِ سوم (سوخت) یا ششی که آخرین مهره را رساند تاسِ دیگر ندارد و پیامی هم نمی‌آید.
   */
  private mnSixFrom = -1;

  constructor(readonly game: string) {}

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    const last = client.board.value;
    if (last && last.game === this.game) this.seq.enqueueSnapshot(() => this.apply(last));
    this.tick = setInterval(() => this.updateTimer(), 200);
    // برای اشکال‌زدایی از کنسول — [[CbOnline.start]]
    if (typeof window !== "undefined") (window as unknown as { __board?: BoardOnline }).__board = this;
  }

  dispose(): void {
    this.quitting = true;
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
    if (this.endHold) clearTimeout(this.endHold);
  }

  seatName(l: number): string {
    const m = this.model.value;
    if (l === 0 && !m?.watching) return "تو";
    return botText(m?.seats[l]?.name || "حریف", m?.seats[l]?.bot);
  }

  // ---------------------------------------------------------------- ورودی

  /** منچ، تخته‌نرد و مارپله: «تاس بریز». */
  /**
   * تاس بریز. swipe (منچ): سرعتِ کشیدنِ انگشت روی تاسِ خودت به خانه بر ثانیه — تاس همان سو پرتاب می‌شود
   * (mnThrow)؛ لمسِ خالی = رو به وسط.
   */
  roll(swipe?: [number, number]): void {
    const m = this.model.value;
    if (!m || !("canRoll" in m) || !m.canRoll) return;
    this.mnSwipe = swipe ?? null;
    this.model.value = { ...m, canRoll: false, mine: false };
    this.send(1);
  }

  /** مارپله، دو تاس: یکی را بردار. */
  chooseDie(d: number): void {
    const m = this.model.value;
    if (!m || m.game !== "marpele" || !m.choose.includes(d)) return;
    this.model.value = { ...m, choose: [], mine: false };
    this.mpPicked.value = d;
    this.send(2, d);
  }

  /** مارپله، سرِ مار با نیرو در دست: ۰ بگذار نیش بزند، ۱ پادزهر، ۲ شمشیر. */
  biteChoice(a: number): void {
    const m = this.model.value;
    if (!m || m.game !== "marpele" || !m.bite) return;
    if ((a === 1 && !m.bite.antidote) || (a === 2 && !m.bite.sword)) return;
    this.model.value = { ...m, bite: null, mine: false };
    this.send(2, a);
  }

  /** واکنش به لحظه‌ی دیگران — کلیدِ آماده روی چتِ میز می‌رود (وب ایموجی می‌کشد، اپ واژه). */
  react(key: string): void {
    this.reactUntil.value = 0;
    client.sendCanned(key);
  }

  /**
   * منچ: لمسِ مهره‌ی `tok` ِ صندلیِ `seat` (پیش‌فرض صاحبِ نوبت — در تیمی ممکن است یارم
   * باشد). مجاز یعنی `seat === owner` و مهره در فهرستِ سرور، با قاعده‌ی خانه/دیوار: لمسِ
   * هر مهره‌ی هم‌جای مهره‌ی فهرست‌شده همان را می‌فرستد (menchPickable).
   */
  pick(tok: number, seat?: number): void {
    const m = this.model.value;
    if (!m || m.game !== "mench") return;
    const send = menchPickable(m, seat ?? m.owner, tok);
    if (send < 0) return;
    this.model.value = { ...m, tokens: [], hints: [], mine: false };
    this.send(2, send);
  }

  /** دوز: ستون. */
  drop(col: number): void {
    const m = this.model.value;
    if (!m || m.game !== "dooz") return;
    if (!m.cols.includes(col)) { if (m.mine) notify("این ستون پر است"); return; }
    this.model.value = { ...m, cols: [], mine: false };
    this.send(2, col);
  }

  /**
   * تخته‌نرد: لمسِ یک خانه (−۱ بار، −۲ جمع). اولین لمس مبدأ را برمی‌دارد؛ اگر فقط یک
   * مقصد دارد همان‌جا می‌رود، وگرنه لمسِ دوم مقصد است.
   */
  nardTap(p: number): void {
    const m = this.model.value;
    if (!m || m.game !== "nard" || m.moves.length === 0) return;
    const from = this.nardFrom.value;
    const froms = [...new Set(m.moves.map((x) => x.from))];
    if (from !== -99) {
      const dests = [...new Set(m.moves.filter((x) => x.from === from).map((x) => x.to))];
      // دوباره همان مبدأ: انتخاب برمی‌دارد
      if (p === from && !dests.includes(p)) { this.nardFrom.value = -99; return; }
      // لمسِ کنارِ مقصد، اگر تنها همسایه‌ی مجاز است، همان مقصد — مثلِ اپ؛ خودِ یک مبدأ اما انتخاب را عوض می‌کند
      const to = dests.includes(p) || froms.includes(p) ? p : nardSnap(p, dests);
      if (dests.includes(to)) { this.nardMove(from, to); return; }
      if (!froms.includes(p)) { this.nudge("فقط مقصدهای سبز"); return; }
    }
    const src = froms.includes(p) ? p : nardSnap(p, froms);
    if (!froms.includes(src)) { this.nudge("فقط خانه‌های روشن حرکت دارند"); return; }
    const tos = [...new Set(m.moves.filter((x) => x.from === src).map((x) => x.to))];
    // یک مقصد بیشتر ندارد و خودش را زد (نه کنارش را): همان لمس حرکت است
    if (src === p && tos.length === 1) { this.nardMove(p, tos[0]!); return; }
    this.nardFrom.value = src;
  }

  /**
   * تخته‌نرد: کشیدن و رهاکردن روی مقصد. مهره همان‌جا می‌ماند (nardHeld) تا حرکت برسد و پروازِ
   * کوتاهش از جای رهاشدن است.
   */
  nardDrop(from: number, to: number, at: [number, number]): boolean {
    const m = this.model.value;
    if (!m || m.game !== "nard" || !m.moves.some((x) => x.from === from && x.to === to)) return false;
    this.nardHeld.value = { from, seat: 0, at };
    this.nardMove(from, to);
    return true;
  }

  /** لمسِ بی‌حرکت: یک تکانِ کوچکِ نشان‌ها و یک خطِ راهنما، به‌جای سکوت. */
  nudge(text: string): void {
    this.nardNudge.value = this.nardNudge.value + 1;
    this.nardHint.value = { text, until: performance.now() + 1600 };
  }

  private nardMove(from: number, to: number): void {
    const m = this.model.value as NardLocal;
    const cands = m.moves.filter((x) => x.from === from && x.to === to);
    if (cands.length === 0) return;
    // چند تاس به یک مقصد (فقط در جمع‌کردن): تاسِ دقیق، وگرنه کوچک‌تر — همان BackgammonController
    const exact = (x: { from: number; to: number; die: number }) => x.to === NARD_OFF && x.die === x.from + 1;
    let pick = cands[0]!;
    for (const x of cands) {
      if (exact(x) && !exact(pick)) pick = x;
      else if (exact(x) === exact(pick) && x.die < pick.die) pick = x;
    }
    this.nardFrom.value = -99;
    this.model.value = { ...m, moves: [], mine: false };
    this.send(2, nardServerPoint(pick.from, m.me), nardServerPoint(pick.to, m.me), pick.die);
  }

  /** دومینو: لمسِ مهره‌ی دست؛ اگر به هر دو سر می‌خورد، سر را هم می‌پرسد. */
  dominoTile(index: number): void {
    const m = this.model.value;
    if (!m || m.game !== "domino" || !m.mine) return;
    const opts = m.plays.filter((p) => p.index === index);
    if (opts.length === 0) { notify("این مهره جور نیست"); return; }
    if (opts.length === 1) { this.dominoPut(index, opts[0]!.side); return; }
    this.dominoSide.value = index;
  }

  dominoPick(side: number): void {
    const m = this.model.value;
    const index = this.dominoSide.value;
    if (!m || m.game !== "domino" || index < 0 || !m.plays.some((p) => p.index === index && p.side === side)) return;
    this.dominoPut(index, side);
  }

  dominoDraw(): void {
    const m = this.model.value;
    if (!m || m.game !== "domino" || !m.canDraw) return;
    this.model.value = { ...m, canDraw: false, plays: [], mine: false };
    this.send(3);
  }

  private dominoPut(index: number, side: number): void {
    const m = this.model.value as DominoLocal;
    this.dominoSide.value = -1;
    this.model.value = { ...m, plays: [], canDraw: false, mine: false };
    this.send(2, index, side);
  }

  private send(kind: number, a = 0, b = 0, c = 0): void {
    if (this.quitting || !this.last || this.last.watching) return;
    this.pending = true;
    this.sentKey = posKey(this.last);
    this.turnEndsAt = 0; this.timer.value = null;
    client.boardMove(kind, a, b, c);
  }

  // ---------------------------------------------------------------- پیام‌های سرور

  private onMessage(env: Envelope): void {
    if (this.quitting) return;
    switch (env.msg.case) {
      case "boardState": {
        const st = env.msg.value;
        if (st.game !== this.game) return;
        this.seq.enqueueSnapshot(() => this.apply(st), this.brk.mustShow(st.brk));
        break;
      }
      case "boardEvent": {
        const ev = env.msg.value;
        this.seq.enqueue(() => this.play(ev), RESULT_EVENTS.has(ev.kind));
        break;
      }
      case "error": this.showError(env.msg.value.code); break;
    }
  }

  /** دومینو: «ادامه» ِ پنلِ پایانِ دور. پنل با شروعِ دورِ بعد خودش بسته می‌شود. */
  nextRound(): void { this.brk.press(); }

  private showError(code: string): void {
    const msg: Record<string, string> = { not_your_turn: "نوبت تو نیست", illegal_move: "این حرکت مجاز نیست" };
    if (!msg[code]) return;
    notify(msg[code]!);
    // حرکت رد شد: همان نمای آخر دوباره ورودی بدهد — ولی تنها گزینه خودکار نرود (حلقه).
    this.pending = false;
    if (this.last && !this.ended.value) { this.manual = true; this.apply(this.last); this.manual = false; }
  }

  /** نمای سرور → مدل. همگام است تا بازگشاییِ بعد از خطا هم از همین راه برود. */
  private apply(st: BoardTableState): void {
    if (!this.carpetApplied) { this.carpetApplied = true; showCarpet(st.carpet); }
    this.last = st;
    if (this.pending && posKey(st) !== this.sentKey) this.pending = false;
    const m = boardLocal(st);
    if (!m) return;
    if (this.pending && m.mine) closeInput(m);   // جوابِ حرکتِ ما هنوز نرسیده
    if (!m.mine) { this.nardFrom.value = -99; this.dominoSide.value = -1; }
    // ورودیِ تازه (حرکتِ رد‌شده یا ادامه‌ی نوبت): مهره‌ی رهاشده دیگر در راه نیست
    if (m.game === "nard" && m.mine && !this.pending) this.nardHeld.value = null;
    if (m.game === "domino" && !m.roundOver) this.roundOver.value = false;
    if (m.game === "domino" && m.chain.length === 0) this.dominoLast.value = null;
    this.brk.update(st.brk, st.seats, st.me, st.watching);
    this.model.value = m;
    // پس از نشستنِ مدل: seatName نامِ صندلی‌ها را از همین نما می‌خواند (پیش‌تر نمای اول «حریف» می‌گفت)
    if (m.game === "nard" && m.opening && !this.nardOpening && !m.over) {
      this.nardOpening = true;
      notify(`تاس آغازین — ${this.seatName(m.turn)} شروع می‌کند`);
      this.nardOpeningThrow(m);
    }

    if (m.game === "mench" && m.mine) {
      // نوبتِ تاس یا انتخابِ خودم رسید: نوارِ واکنش و خطِ گذرا همان لحظه می‌روند
      this.reactUntil.value = 0;
      this.mnNote.value = null;
    }
    if (m.game === "mench" && this.mnSixFrom >= 0) {
      const who = this.mnSixFrom;
      this.mnSixFrom = -1;
      if (!m.over && m.turn === who && m.phase === 0) {
        if (who === 0 && !m.watching) notify("شش! یک تاس دیگر");
        else this.note(`${this.seatName(who)} — شش! یک تاس دیگر`);
      }
    }
    // منچ: تنها مهره‌ی مجاز خودش می‌رود، مثلِ میزِ اپ — با یکی‌شدنِ خانه‌نشین‌ها در
    // فهرستِ سرور (§4.5) همان شروعِ بازی هم خودکار است
    if (m.game === "mench" && m.tokens.length === 1 && !this.manual) this.pick(m.tokens[0]!);

    const left = st.turnDeadlineMs / 1000;
    if (m.mine && left > 0.1) {
      this.turnTotal = Math.max(this.turnTotal, left);
      this.turnEndsAt = performance.now() + left * 1000;
    } else { this.turnEndsAt = 0; this.turnTotal = 0; this.timer.value = null; }

    if (st.over && !this.ended.value && !this.endHold) {
      // دوز: اول خطِ چهارتای برنده روی خودِ تخته کشیده می‌شود و بقیه‌ی مهره‌ها کم‌رنگ — پنلِ پایان پس از دیدنش (کاربر،
      // ۲۷ اکتبر ۲۰۲۶: «باید نشان بدهی با کدام مهره‌ها برد یا باخت»)؛ پیش از این پنل همان لحظه رویش می‌آمد
      if (m.game === "dooz" && m.line.length > 0) {
        this.endHold = setTimeout(() => { this.endHold = 0; if (!this.quitting) this.ended.value = true; }, ms(DOOZ_LINE_HOLD));
      } else {
        this.ended.value = true;
        if (m.game === "nard" && !m.watching && m.winner === 0) this.confetti.value = this.confetti.value + 1;
      }
    }
  }

  private updateTimer(): void {
    const m = this.model.value;
    if (this.turnEndsAt <= 0 || !m?.mine) { if (this.timer.value) this.timer.value = null; return; }
    const left = (this.turnEndsAt - performance.now()) / 1000;
    if (left <= 0) { this.turnEndsAt = 0; this.timer.value = null; return; }
    this.timer.value = { left, total: Math.max(left, this.turnTotal) };
  }

  private async play(ev: BoardEvent): Promise<void> {
    this.pending = false;   // سرور چیزی را اعمال کرد
    const m0 = this.model.value;
    if (!m0) return;        // نمای بعدی همه‌چیز را می‌آورد
    const seat = toLocal(ev.seat, m0.me, m0.n);
    switch (ev.kind) {
      case "seat_bot": notify(`${this.seatName(seat)} رفت — ربات جایش نشست`); return;
      case "seat_reconnect": notify(`${this.seatName(seat)} برگشت`); return;
    }
    switch (m0.game) {
      case "mench": await this.playMench(ev, m0, seat); break;
      case "dooz": await this.playDooz(ev, m0, seat); break;
      case "nard": await this.playNard(ev, m0, seat); break;
      case "domino": await this.playDomino(ev, m0, seat); break;
      case "marpele": await this.playMarpele(ev, m0, seat); break;
    }
  }

  /** پرتابِ سه‌بعدیِ تاس (Dice3D) ۸۲۰ms است؛ مکثِ سرور برای «roll» ۹۰۰ms — پس صف عقب نمی‌افتد. */
  private async rollAnim(): Promise<void> {
    this.rolling.value = true;
    await wait(820);
    this.rolling.value = false;
    await wait(40);
  }

  /** منچ: خطِ وضعیتِ گذرا — جای توستی که برای هر تاسِ دیگران صفحه را شلوغ می‌کرد. */
  private note(text: string, life = 2600): void {
    // نوارِ واکنش جای خطِ وضعیت را گرفته باشد، عمرِ خط از پایانِ نوار شمرده می‌شود — وگرنه
    // «سارا — جایزه: یک تاسِ دیگر» که درست بعد از زدن می‌آید زیرِ نوارِ سه‌ثانیه‌ای تمام می‌شد
    const from = Math.max(performance.now(), this.reactUntil.value);
    this.mnNote.value = { text, until: from + ms(life) };
  }

  /** منچ: حبابِ روی پلاکِ یک صندلی. */
  private bubble(seat: number, text: string, life = 1600): void {
    this.mnBubble.value = { seat, text, until: performance.now() + ms(life), id: ++this.fxId };
  }

  /**
   * منچ: نوارِ واکنشِ یک‌لمسی بعد از زدن — برای **همه‌ی** آدم‌های سرِ میز، زننده و زده‌شده
   * هم (برخلافِ مارپله که صاحبِ لحظه را کنار می‌گذارد)، جز تماشاچی. سه ثانیه، یکی در هر
   * لحظه، و همان لحظه که نوبتِ تاس یا انتخابِ خودم برسد می‌رود (apply).
   */
  private offerMenchReaction(): void {
    const m = this.model.value;
    if (!m || m.watching || m.over) return;
    this.reactUntil.value = performance.now() + 3000;
  }

  /**
   * رویدادهای منچ (§7): roll، retry، pass، forfeit، move (با Data = تاس‌ریز وقتی برای یارش
   * بازی می‌کند)، hit، extra، place، match_over. قاعده‌ای این‌جا نیست؛ فقط نمایش. صندلی‌ها
   * (Seat، hit.B، move.Data[0]) همه به ترتیبِ سرورند و این‌جا به صندلیِ محلی می‌چرخند.
   */
  private async playMench(ev: BoardEvent, m0: MenchLocal, seat: number): Promise<void> {
    const me = !m0.watching;
    const mine = me && seat === 0;
    switch (ev.kind) {
      case "roll":
        this.mnDie = ev.data[0] ?? m0.die;
        this.mnForfeit = false;
        this.model.value = { ...m0, turn: seat, owner: seat === m0.turn ? m0.owner : seat, die: ev.data[0] ?? m0.die,
          canRoll: false, tokens: [], hints: [], mine: false };
        if (reducedMotion()) { await this.rollAnim(); break; }
        {
          // تاس روی تخته پرتاب می‌شود: تاسِ من به سوی کشیدنِ انگشت، بقیه رو به وسط؛ به قاب می‌خورد و روی عدد می‌ایستد
          const corner = m0.corner[seat] ?? 0;
          const v = throwVelocity(corner, seat === 0 && me ? this.mnSwipe : null);
          if (seat === 0) this.mnSwipe = null;
          const T = Math.min(ms(900), 1200), hold = Math.min(ms(260), 400);
          this.mnThrow.value = { seat, corner, v, value: ev.data[0] ?? m0.die, ms: T, hold, id: ++this.fxId };
          if (seat === 0 && me) try { navigator.vibrate?.(15); } catch { /* مرورگرِ بی‌لرزش */ }
          await sleep(T + hold);
          this.mnThrow.value = null;
        }
        break;
      case "retry": {
        // «سه شانس»: هنوز مهره‌ای بیرون نیست و شش نیامد — همان صندلی دوباره می‌ریزد. A = شانسِ مانده.
        const cur = this.model.value as MenchLocal;
        this.model.value = { ...cur, tries: Math.max(0, ev.a) };
        const which = ev.a >= 2 ? "شانس دوم" : "شانس سوم";
        if (mine) notify(which); else this.note(`${this.seatName(seat)} — ${which}`);
        await wait(350);
        break;
      }
      case "forfeit":
        this.mnForfeit = true;
        notify(mine ? "سه‌تا شش! نوبتت سوخت" : `${this.seatName(seat)} سه‌تا شش آورد — نوبتش سوخت`);
        await wait(300);
        break;
      case "pass": {
        // قاعده‌ی §7: pass ِ بی forfeit که همان صندلی بعدش در فاز ۰ بماند = شش بی‌حرکت. سرور
        // فقط با ششِ بی‌حرکت نوبت را نگه می‌دارد (§5.2)، پس نمای بعدی را نباید منتظر ماند:
        // تاسِ همین roll بس است.
        const again = !this.mnForfeit && this.mnDie === 6;
        notify(mine ? (again ? "حرکتی نداری — شش بود، دوباره بریز" : "حرکتی نداری")
          : `${this.seatName(seat)} حرکتی ندارد${again ? " — شش بود، دوباره می‌ریزد" : ""}`);
        this.mnForfeit = false;
        await wait(450);
        break;
      }
      case "move": {
        const tok = ev.a, from = ev.b, to = ev.c;
        if (seat < 0 || seat >= m0.n || tok < 0 || tok > 3) return;
        // Data[0] فقط وقتی هست که تاس‌ریز صاحبِ مهره نباشد (تیمی: مهره‌هایش رسیده و برای یارش می‌ریزد)
        const rs = ev.data.length > 0 ? toLocal(ev.data[0]!, m0.me, m0.n) : seat;
        const roller = rs >= 0 && rs < m0.n ? rs : seat;
        this.mnRoller = roller;
        if (roller !== seat) this.note(me && roller === 0 ? "برای یارت بازی می‌کنی" : `${this.seatName(roller)} برای یارش بازی می‌کند`);
        // «کارِ من»: مهره‌ی خودم، یا مهره‌ی یارم که من برایش ریختم — صداهای شخصی فقط برای این
        const byMe = me && (seat === 0 || roller === 0);
        const corner = m0.corner[seat] ?? 0, color = menchColor(corner, m0.shift);
        let step = 0;
        for (const p of menchPath(from, to)) {
          const cur = this.model.value as MenchLocal;
          const pos = cur.pos.map((r) => [...r]);
          pos[seat]![tok] = p;
          this.mnWalk.value = { seat, tok, step: ++step, base: from === BASE };
          // «تُک» ِ چوبی برای هر گام (جای صدای کارتِ پیشین، tools/mench_sfx.py)؛ کمی زیرتر با هر خانه، کارِ خودت بلندتر
          sfx("mn_step", { delay: ms(from === BASE ? 210 : 110), volume: byMe ? 0.6 : 0.38, rate: Math.min(1.35, 0.94 + step * 0.035) });   // سرِ فرودِ گام
          this.model.value = { ...cur, pos, tokens: [], hints: [], mine: false };
          if (from === BASE) {
            // بیرون‌آمدن از خانه: جرقه‌ی کوچک برای همه، ووش فقط برای خودم
            const [x, y] = menchTokenXY(corner, tok, 0);
            this.mnFx.value = { kind: "burst", x, y, color, id: ++this.fxId };
            if (byMe) synth("whoosh", { volume: 0.45 });
          }
          await wait(from === BASE ? 240 : 130);
        }
        this.mnWalk.value = null;
        if (to === GOAL) {
          this.mnPose.value = { key: `${seat}-${tok}`, pose: "cheer", until: performance.now() + ms(1400) };
          // رسیدن: فانفار برای خودم، برقِ کوچک برای بقیه
          if (byMe) menchSound("fanfare", { volume: 0.8 });
          else { const [x, y] = menchTokenXY(corner, tok, GOAL); this.mnFx.value = { kind: "sparkle", x, y, color, id: ++this.fxId }; }
        } else if (from !== BASE && from <= MAIN_LAST && to >= HOME0 && byMe) synth("chime", { volume: 0.6 });   // ورود به راهرو
        this.mnLand = { seat, tok, to };
        this.mnHits = 0;
        this.mnSixFrom = this.mnDie === 6 ? roller : -1;
        break;
      }
      case "hit": {
        const tok = ev.a, hitter = toLocal(ev.b, m0.me, m0.n);
        if (seat < 0 || seat >= m0.n || tok < 0 || tok > 3) return;
        const cur0 = this.model.value as MenchLocal;
        const vc = cur0.corner[seat] ?? 0;
        // پرواز از خانه‌ی فرودِ همین حرکت: مهره‌ی زده‌شده هنوز همان‌جاست (نما بعد از رویدادها
        // می‌رسد)؛ اگر نمای تازه‌تر زودتر آمده بود، از خانه‌ی فرودِ آخرین حرکت
        const vp = cur0.pos[seat]?.[tok] ?? BASE;
        const land = this.mnLand;
        const from: [number, number] | null = vp >= 0 && vp <= MAIN_LAST ? menchTokenXY(vc, tok, vp)
          : land && land.to >= 0 && land.to <= MAIN_LAST ? menchTokenXY(cur0.corner[land.seat] ?? 0, 0, land.to) : null;
        const involved = me && (seat === 0 || hitter === 0 || this.mnRoller === 0);
        // صحنه‌ی چوبدستی فقط برای اولین زده‌شده‌ی همین حرکت و وقتی زننده همان‌جا فرود آمده
        const att = this.mnHits++ === 0 && from && land && land.to >= 0 && land.to <= MAIN_LAST && !reducedMotion() ? land : null;
        const bonkMs = Math.min(ms(820), 1000);
        if (att && from) {
          const ci = (c: number) => (((c + cur0.shift) % 4) + 4) % 4;
          this.mnBonk.value = { seat: att.seat, tok: att.tok, vseat: seat, vtok: tok, x: from[0], y: from[1],
            ci: ci(cur0.corner[att.seat] ?? 0), vci: ci(vc), girl: cur0.seats[att.seat]?.piece === 1, vgirl: cur0.seats[seat]?.piece === 1,
            ms: bonkMs, id: ++this.fxId };
          await sleep(bonkMs * BONK_IMPACT);
        } else {
          // لحظه‌ی مکث پیش از ضربه — ۱۸۰ تا ۲۵۰ میلی‌ثانیه‌ی §12، **مطلق**: «خیلی آرام» (۲٫۶×) آن را
          // به نیم‌ثانیه‌ی کش‌دار می‌برد و ضرب از دست می‌رفت؛ فقط دویدنِ صف (K < ۱) کوتاهش می‌کند
          this.mnPose.value = { key: `${seat}-${tok}`, pose: "scared", until: performance.now() + 900 };
          await sleep(Math.min(ms(220), 220));
        }
        if (involved) {
          this.mnShake.value = this.mnShake.value + 1;
          try { navigator.vibrate?.(60); } catch { /* مرورگرِ بی‌لرزش */ }
        }
        menchSound(mine ? "hurt" : "hit", { volume: involved ? 0.9 : 0.55 });
        this.bubble(seat, "😱");
        notify(me && hitter === 0 ? `مهره‌ی ${this.seatName(seat)} را زدی!`
          : mine ? `${this.seatName(hitter)} مهره‌ات را زد!`
          : `${this.seatName(hitter)} مهره‌ی ${this.seatName(seat)} را زد!`);
        if (att) await sleep(bonkMs * (1 - BONK_IMPACT));   // ستاره‌ها و له‌شدن، بعد پرواز
        if (from) {
          // پرواز با سرعتِ بازیکن کُند می‌شود ولی نه بیش از ۹۰۰ms — CSS همین عدد را می‌گیرد (--fly)
          const flyMs = Math.min(ms(600), 900);
          const start: [number, number] = att ? [from[0] + BONK_DX, from[1]] : from;
          this.mnFly.value = { seat, tok, from: start, to: menchTokenXY(vc, tok, BASE), color: menchColor(vc, cur0.shift), ms: flyMs, id: ++this.fxId };
          this.mnBonk.value = null;
          await sleep(flyMs);
        }
        this.mnBonk.value = null;
        const cur = this.model.value as MenchLocal;
        const pos = cur.pos.map((r) => [...r]);
        pos[seat]![tok] = BASE;
        this.model.value = { ...cur, pos };
        // یک فریم پنهان در خانه، بعد پیدا — وگرنه گذارِ CSS ِ مهره پروازِ دوم می‌ساخت
        await wait(40);
        this.mnFly.value = null;
        this.offerMenchReaction();
        await wait(260);
        break;
      }
      case "extra":
        // «تاسِ جایزه»: زدن (A = ۲) یا رسیدن (A = ۳) یک تاسِ دیگر داد
        if (mine) notify("جایزه: یک تاس دیگر"); else this.note(`${this.seatName(seat)} — جایزه: یک تاس دیگر`);
        await wait(500);
        break;
      case "place": {
        // «تا نفرِ آخر»: رتبه‌ی یک صندلی معلوم شد
        const cur = this.model.value as MenchLocal;
        if (seat < 0 || seat >= cur.n) return;
        const rank = [...cur.rank]; rank[seat] = ev.a;
        this.model.value = { ...cur, rank };
        notify(mine ? `نفر ${menchOrdinal(ev.a)} شدی` : `${this.seatName(seat)} نفر ${menchOrdinal(ev.a)} شد`);
        if (mine && ev.a === 1) this.confetti.value = this.confetti.value + 1;
        await wait(mine ? 900 : 500);
        break;
      }
      case "gate": {
        // «شکارچی»: مهره‌ها پشتِ دروازه گیر کرده بودند و تاسی حرکت نمی‌داد — دروازه باز شد
        const cur = this.model.value as MenchLocal;
        if (seat >= 0 && seat < cur.n) { const gate = [...cur.gate]; gate[seat] = true; this.model.value = { ...cur, gate }; }
        if (mine) notify("گیر کرده بودی؛ دروازه باز شد"); else this.note(`${this.seatName(seat)} گیر کرده بود؛ دروازه‌اش باز شد`);
        await wait(500);
        break;
      }
      case "double": {
        // «دوبرابر»: همین تاس دو برابر می‌رود
        const cur = this.model.value as MenchLocal;
        const dbl = [...cur.dbl];
        if (seat >= 0 && seat < cur.n) dbl[seat] = false;
        this.model.value = { ...cur, mult: 2, dbl };
        if (mine) notify("تاس دوبرابر!"); else this.note(`${this.seatName(seat)} — تاس دوبرابر`);
        await wait(400);
        break;
      }
      case "power": {
        // نیرو برداشته شد (A نیرو، B خانه‌ی درخشان): خانه خالی، سپر و دوبرابر در دستِ صاحب، جابه‌جایی همان دم (swap)
        const cur = this.model.value as MenchLocal;
        const cells = [...cur.cells], shield = [...cur.shield], dbl = [...cur.dbl];
        if (ev.b >= 0 && ev.b < 4) cells[ev.b] = 0;
        if (seat >= 0 && seat < cur.n) {
          if (ev.a === POWER_SHIELD) shield[seat] = true;
          if (ev.a === POWER_DOUBLE) dbl[seat] = true;
        }
        this.model.value = { ...cur, cells, shield, dbl };
        const [x, y] = menchPowerXY(Math.max(0, Math.min(3, ev.b)), cur.shift);
        this.mnFx.value = { kind: "sparkle", x, y, color: "#ffe36a", id: ++this.fxId };
        if (mine) synth("chime", { volume: 0.6 });
        const name = POWER_FA[ev.a as 1 | 2 | 3] ?? "نیرو";
        if (mine) notify(`نیرو گرفتی: ${name}`); else this.note(`${this.seatName(seat)} — نیرو: ${name}`);
        await wait(550);
        break;
      }
      case "shield": {
        // سپر شکست: مهره‌ی seat (A) زده نشد؛ B = زننده
        const hitter = toLocal(ev.b, m0.me, m0.n);
        const cur = this.model.value as MenchLocal;
        if (seat >= 0 && seat < cur.n) { const shield = [...cur.shield]; shield[seat] = false; this.model.value = { ...cur, shield }; }
        this.bubble(seat, "سپر!");
        notify(mine ? "سپرت مهره‌ات را نجات داد" : me && hitter === 0 ? `سپر ${this.seatName(seat)} مهره‌اش را نجات داد`
          : `سپر مهره‌ی ${this.seatName(seat)} را نجات داد`);
        await wait(600);
        break;
      }
      case "swap": {
        // جابه‌جایی: مهره‌ی A ِ seat با مهره‌ی C ِ حریفِ B جا عوض می‌کند — data = [از، به، جای تازه‌ی حریف]
        const opp = toLocal(ev.b, m0.me, m0.n);
        const cur = this.model.value as MenchLocal;
        if (seat >= 0 && seat < cur.n && opp >= 0 && opp < cur.n && ev.a >= 0 && ev.a < 4 && ev.c >= 0 && ev.c < 4 && ev.data.length >= 3) {
          const pos = cur.pos.map((r) => [...r]);
          pos[seat]![ev.a] = ev.data[1]!;
          pos[opp]![ev.c] = ev.data[2]!;
          this.model.value = { ...cur, pos };
          const [x, y] = menchTokenXY(cur.corner[seat] ?? 0, ev.a, ev.data[1]!);
          this.mnFx.value = { kind: "burst", x, y, color: menchColor(cur.corner[seat] ?? 0, cur.shift), id: ++this.fxId };
        }
        // جابه‌جایی: دو «تُک» ِ پشتِ هم، هر مهره یکی
        sfx("mn_step", { volume: 0.6 }); sfx("mn_step", { delay: 140, volume: 0.6, rate: 1.18 });
        notify(mine ? `با ${this.seatName(opp)} جا عوض کردی!` : me && opp === 0 ? `${this.seatName(seat)} با تو جا عوض کرد!`
          : `${this.seatName(seat)} با ${this.seatName(opp)} جا عوض کرد`);
        await wait(700);
        break;
      }
      case "timeup":
        // «زمان‌دار»: زمان تمام شد — پایان و رتبه‌ها با match_over و نمای بعدی
        notify("وقت تمام شد!");
        await wait(700);
        break;
      case "match_over": {
        // پنلِ پایان از روی خودِ نما (over، رتبه‌ها)؛ این‌جا فقط جشن — برای برنده (تیمی: هر دو یار)
        const won = me && (seat === 0 || (m0.opt.teams && seat === menchPartner(0)));
        if (won) this.confetti.value = this.confetti.value + 1;
        break;
      }
    }
  }

  private async playDooz(ev: BoardEvent, m0: DoozLocal, seat: number): Promise<void> {
    if (ev.kind !== "move") return;   // match_over: پایان از روی نما — مساوی هم
    const col = ev.a, row = ev.b;
    if (col < 0 || col >= m0.cells.length || row < 0 || row >= (m0.cells[0]?.length ?? 0)) return;
    if (m0.cells[col]![row]! >= 0) return;   // نمای تازه‌تر زودتر رسیده بود
    const cells = m0.cells.map((c) => [...c]);
    cells[col]![row] = seat;
    const heights = [...m0.heights];
    heights[col] = Math.max(heights[col]!, row + 1);
    this.dropping.value = `${col}:${row}`;
    this.model.value = { ...m0, cells, heights, cols: [], mine: false };
    // برخوردِ مهره با کفِ ستون: «تَق» ِ پلاستیکِ خودِ دوز و جهشِ کوچکش، یکی از چهار گونه (tools/board_sfx.py)، درست سرِ
    // پایانِ btFall (۴۲۰ میلی‌ثانیه × سرعت؛ ضربه ۴ میلی‌ثانیه پس از آغازِ فایل است) — نه move، که صدای کارت است
    sfx(`dz_drop_${1 + Math.floor(Math.random() * 4)}` as Sfx, { delay: ms(416), volume: 0.6, rate: 0.96 + Math.random() * 0.08 });
    await wait(460);
    this.dropping.value = "";
  }

  private async playNard(ev: BoardEvent, m0: NardLocal, seat: number): Promise<void> {
    const me = !m0.watching;
    const mapP = (p: number) => nardServerPoint(p, m0.me);   // آینه دوطرفه است
    switch (ev.kind) {
      case "roll": {
        const d1 = ev.data[0] ?? 1, d2 = ev.data[1] ?? 1;
        // phase ۱: تاس‌های این نوبت در جعبه‌ی ریزنده بمانند تا نمای بعدی برسد
        this.model.value = { ...m0, turn: seat, rolled: [d1, d2], dice: d1 === d2 ? [d1, d1, d1, d1] : [d1, d2], phase: 1,
          canRoll: false, moves: [], mine: false, opening: false };
        if (reducedMotion()) { await this.rollAnim(); break; }
        // همان پرتابِ منچ و مارپله: تاس‌های من به سوی کشیدنِ انگشت، حریف رو به وسط؛ دو تاس از کنارِ هم، دومی کمی کج‌تر و کندتر
        const corner = nardCorner(seat);
        const v = throwVelocity(corner, seat === 0 && me ? this.mnSwipe : null, Math.random, NARD_FIELD);
        if (seat === 0) this.mnSwipe = null;
        const turn = (a: number, k: number): [number, number] =>
          [(v[0] * Math.cos(a) - v[1] * Math.sin(a)) * k, (v[0] * Math.sin(a) + v[1] * Math.cos(a)) * k];
        const throws = [d1, d2].map((value, i) => ({ corner, v: i === 0 ? v : turn(0.3, 0.86), dx: i === 0 ? -0.8 : 0.8, value }));
        // مکثِ سرور برای roll ۹۰۰ms است؛ پرتاب کمی کوتاه‌تر از منچ تا صف عقب نیفتد
        const T = Math.min(ms(760), 1000), hold = Math.min(ms(240), 360);
        this.nardThrow.value = { seats: [seat], throws, ms: T, hold, id: ++this.fxId };
        if (seat === 0 && me) try { navigator.vibrate?.(15); } catch { /* مرورگرِ بی‌لرزش */ }
        await sleep(T + hold);
        this.nardThrow.value = null;
        break;
      }
      case "pass":
        notify(me && seat === 0 ? "حرکتی نداری" : `${this.seatName(seat)} حرکتی ندارد`);
        await wait(600);
        break;
      case "move": {
        const from = mapP(ev.a), to = mapP(ev.b), hit = (ev.data[0] ?? 0) === 1;
        if (seat < 0 || seat > 1) return;
        await this.nardMoveScene(seat, from, to, ev.c, hit);
        break;
      }
    }
  }

  /**
   * تاسِ آغازین (رویدادِ roll ندارد؛ نمای اول با opening می‌آید): هر بازیکن یک تاس از گوشه‌ی خودش — بیشتر شروع می‌کند و
   * هر دو تاس را بازی می‌کند. rolled به ترتیبِ صندلیِ سرور است (Die1 مالِ صندلیِ ۰). جعبه‌ها تا پایانِ پرتاب خالی‌اند.
   */
  private nardOpeningThrow(m: NardLocal): void {
    if (reducedMotion() || !m.rolled[0] || !m.rolled[1]) return;
    const mineAt = m.me & 1;
    const T = Math.min(ms(760), 1000), hold = Math.min(ms(240), 360);
    const throws = [0, 1].map((s) => {
      const corner = nardCorner(s);
      return { corner, v: throwVelocity(corner, null, Math.random, NARD_FIELD), dx: 0, value: m.rolled[s === 0 ? mineAt : 1 - mineAt]! };
    });
    const id = ++this.fxId;
    this.nardThrow.value = { seats: [0, 1], throws, ms: T, hold, id };
    setTimeout(() => { if (this.nardThrow.value?.id === id) this.nardThrow.value = null; }, T + hold);
  }

  /**
   * یک حرکتِ تخته‌نرد، گام‌به‌گام مثلِ BackgammonView.AnimMove ِ اپ: برداشتن از مبدأ (یا از
   * جای رهاشدن)، پروازِ کمانی، تقِ فرود؛ زدن = جای تک‌مهره را می‌گیرد، حلقه‌ی نارنجی، مکثِ
   * کوتاه و پروازِ چرخانِ زده‌شده تا بار. با «حرکتِ کمتر» ِ سیستم بی پرواز. کلِ کار زیرِ مکثِ
   * سرور (boardPace ِ nard: ۵۲۰ و با زدن ۱۰۰۰) می‌ماند.
   */
  private async nardMoveScene(seat: number, from: number, to: number, die: number, hit: boolean): Promise<void> {
    const reduce = reducedMotion();
    const cur = this.model.value as NardLocal;
    const held = this.nardHeld.value;
    this.nardHeld.value = null;
    const sg = seat === 0 ? 1 : -1;
    const start = held && held.from === from && held.seat === seat ? held.at : nardTopXY(cur, from, seat);
    const pts = [...cur.pts];
    const bar: [number, number] = [cur.bar[0], cur.bar[1]];
    const off: [number, number] = [cur.off[0], cur.off[1]];
    if (from === NARD_BAR) bar[seat]--; else pts[from] = pts[from]! - sg;
    const dice = [...cur.dice];
    const at = dice.indexOf(die);
    if (at >= 0) dice.splice(at, 1);
    const land = hit && to >= 0 ? nardChkXY(to, 0) : nardLandXY({ pts, bar, off }, to, seat);
    this.nardFrom.value = -99;
    this.model.value = { ...cur, pts: [...pts], bar: [bar[0], bar[1]], off: [off[0], off[1]], dice, moves: [], mine: false };

    if (!reduce) {
      const dist = Math.hypot(land[0] - start[0], land[1] - start[1]);
      const base = 200 + 120 * Math.min(1, dist / (9 * NCHK));
      this.nardFly.value = { from: start, to: land, seat, ms: ms(base), hit: false, off: to === NARD_OFF, id: ++this.fxId };
      await wait(base);
      this.nardFly.value = null;
    }
    if (to === NARD_OFF) off[seat]++;
    else pts[to] = hit ? sg : pts[to]! + sg;
    this.model.value = { ...(this.model.value as NardLocal), pts: [...pts], off: [off[0], off[1]] };
    // مهره روی خانه: «تَق» ِ چوبیِ خودِ نرد، یکی از چهار گونه و کمی زیر و بم — نه dice_clack ِ تاس (tools/board_sfx.py)
    sfx(`nd_place_${1 + Math.floor(Math.random() * 4)}` as Sfx, { volume: 0.6, rate: 0.95 + Math.random() * 0.1 });
    if (!hit) { await wait(reduce ? 120 : 40); return; }

    const vic = 1 - seat;
    // زده شدی: ضربه‌ی سنگین؛ بقیه (زدی، یا تماشا): همان «پاپِ» زدنِ منچ
    menchSound(!cur.watching && vic === 0 ? "hurt" : "hit", { volume: 0.8 });
    // واکنشِ یک‌لمسی برای هر دو، مثلِ زدنِ منچ — جای خطِ وضعیت، وقتی نوبتِ حرکتِ خودت نیست
    this.offerMenchReaction();
    if (!reduce) {
      this.nardRing.value = { x: land[0], y: land[1], id: ++this.fxId };
      await wait(140);
      const dest = nardBarXY(vic, Math.min(bar[vic], 3));
      this.nardFly.value = { from: land, to: dest, seat: vic, ms: ms(380), hit: true, off: false, id: ++this.fxId };
      await wait(380);
      this.nardFly.value = null;
    }
    bar[vic]++;
    this.model.value = { ...(this.model.value as NardLocal), bar: [bar[0], bar[1]] };
    await wait(60);
  }

  private async playDomino(ev: BoardEvent, m0: DominoLocal, seat: number): Promise<void> {
    const me = !m0.watching;
    switch (ev.kind) {
      case "play": {
        const a = ev.a, b = ev.b, side = ev.c;
        const cur = this.model.value as DominoLocal;
        let chain = [...cur.chain], left = cur.leftEnd, right = cur.rightEnd, center = cur.center;
        const first = side < 0 || chain.length === 0;
        if (first) { chain = [[a, b]]; left = a; right = b; center = 0; }
        else if (side === 1) { const outer = a === right ? b : a; chain.push([right, outer]); right = outer; }
        else { const outer = a === left ? b : a; chain.unshift([outer, left]); left = outer; center++; }
        this.dominoLast.value = { side: first ? -1 : side, a, b };
        const counts = [...cur.counts];
        if (seat >= 0 && seat < counts.length) counts[seat] = Math.max(0, counts[seat]! - 1);
        let hand = cur.hand;
        if (seat === 0 && me) {
          const i = hand.findIndex(([x, y]) => (x === a && y === b) || (x === b && y === a));
          if (i >= 0) hand = [...hand.slice(0, i), ...hand.slice(i + 1)];
        }
        this.dominoSide.value = -1;
        this.model.value = { ...cur, chain, center, leftEnd: left, rightEnd: right, counts, hand, plays: [], canDraw: false, mine: false };
        // سنگ روی میز: «تَق» ِ خودِ دومینو (tools/board_sfx.py)، سرِ فرودِ dmDrop (۹۹ از ۳۲۰ میلی‌ثانیه × سرعت) — نه dice_clack ِ تاس
        sfx("dm_place", { delay: ms(96), volume: 0.6, rate: 0.94 + Math.random() * 0.12 });
        await wait(380);
        break;
      }
      case "draw": {
        const cur = this.model.value as DominoLocal;
        const counts = [...cur.counts];
        if (seat >= 0 && seat < counts.length) counts[seat] = counts[seat]! + 1;
        this.model.value = { ...cur, yard: ev.a, counts, plays: [], canDraw: false, mine: false };
        await wait(260);
        break;
      }
      case "pass":
        notify(me && seat === 0 ? "جور نداشتی و بازار خالی است — رد" : `${this.seatName(seat)} رد داد`);
        await wait(450);
        break;
      case "round_over": {
        const cur = this.model.value as DominoLocal;
        const w = ev.data[0] ?? -1, pts = ev.data[1] ?? 0;
        const winner = w >= 0 ? toLocal(w, cur.me, cur.n) : -1;
        const score = [...cur.score];
        if (winner >= 0) score[winner] = score[winner]! + pts;   // پنل عددِ درست را پیش از نمای تازه نشان دهد
        this.model.value = { ...cur, roundOver: true, roundWinner: winner, roundPoints: pts, blocked: (ev.data[2] ?? 0) === 1,
          score, mine: false, plays: [], canDraw: false };
        this.roundOver.value = true;
        await wait(2400);
        break;
      }
      case "round_start":
        // دومینو: پنلِ پایانِ دور دستِ کم PANEL_MIN_MS دیده شود، حتی اگر صفحه پشتِ سرور مانده — [[BreakWatch.keep]].
        await this.brk.keep(this.seq, undefined, this.roundOver.value);
        if (this.quitting) return;
        this.roundOver.value = false;
        break;
    }
  }

  private fx(kind: "bite" | "sword" | "antidote" | "pickup", cell: number, magic?: number): void {
    this.mpFx.value = { kind, cell, magic, id: ++this.fxId };
  }

  private setPos(seat: number, p: number): void {
    const cur = this.model.value as MarpeleLocal;
    const pos = [...cur.pos]; pos[seat] = p;
    this.model.value = { ...cur, pos, mine: false, canRoll: false, choose: [], bite: null };
  }

  /** نوبت‌های ماشینِ یک صندلی روی نما (سوارشدن، نیش، زده‌شدن) — نمای بعدیِ سرور به‌هرحال درستش را می‌آورد. */
  private setCar(seat: number, car: number): void {
    const cur = this.model.value as MarpeleLocal;
    const p = cur.powers[seat];
    if (!p || p.car === car) return;
    const powers = [...cur.powers]; powers[seat] = { ...p, car };
    this.model.value = { ...cur, powers };
  }

  /**
   * پادزهر یا شمشیرِ در دستِ یک صندلی روی نما، همان لحظه‌ی برداشتن و به‌کاربردن — تا آدمک همان‌جا زره بپوشد یا شیشه
   * بیاویزد و برعکس، نه بعد از نمای بعدیِ سرور (که درستش را به‌هرحال می‌آورد).
   */
  private setPower(seat: number, kind: number, delta: number): void {
    const cur = this.model.value as MarpeleLocal;
    const p = cur.powers[seat];
    if (!p || (kind !== MAGIC_ANTIDOTE && kind !== MAGIC_SWORD)) return;
    const key = kind === MAGIC_SWORD ? "sword" : "antidote";
    const n = Math.max(0, Math.min(MAX_POWER, p[key] + delta));
    if (n === p[key]) return;
    const powers = [...cur.powers]; powers[seat] = { ...p, [key]: n };
    this.model.value = { ...cur, powers };
  }

  /** لحظه‌ی کوبیدن: زده‌شده‌ها به «شروع» برمی‌گردند (و ماشینشان می‌رود)، کمانِ پرواز و صدای ضربه. */
  private async ramMoment(seat: number, cell: number, victims: number[]): Promise<void> {
    const cur = this.model.value as MarpeleLocal;
    const vs = victims.filter((v) => v >= 0 && v < cur.n);
    if (!vs.length) return;
    const flights = vs.map((v) => ({ s: v, from: marpeleXY(cur.pos[v] || cell), to: [0.6 + v * 0.8, 10.45] as [number, number] }));
    const pos = [...cur.pos], powers = [...cur.powers];
    for (const v of vs) { pos[v] = 0; if (powers[v]) powers[v] = { ...powers[v]!, car: 0 }; }
    this.model.value = { ...cur, pos, powers };
    this.mpRam.value = { seat, cell, victims: flights, id: ++this.fxId };
    synth("bang", { volume: 0.7 });
    try { navigator.vibrate?.(30); } catch { /* مرورگرِ بی‌لرزش */ }
    for (const v of vs) { notify(`${this.seatName(seat)} با ماشین به ${this.seatName(v)} زد! برگشت سر شروع`); this.offerReaction(v); }
    await wait(700);
    this.mpRam.value = null;
  }

  /** نوارِ واکنش برای همه جز خودِ کسی که لحظه مالِ اوست — چهار ثانیه. */
  private offerReaction(seat: number): void {
    const m = this.model.value;
    if (!m || m.watching || seat === 0) return;
    this.reactUntil.value = performance.now() + 4000;
  }

  /**
   * لحظه‌ی شمشیر: آدمکِ زره‌پوش کنارِ سرِ مار شمشیر را بالا می‌برد و فرود می‌آورد (MarpeleStrike)؛ سرِ ضربه برق و جرقه،
   * «شَق» ِ شمشیر و مار همان‌جا بی‌جان می‌شود. «حرکتِ کمتر»: فقط برق و مارِ بی‌جان. مکثِ سرور (boardPace ِ sword) ۱۶۰۰ میلی‌ثانیه است.
   */
  private async swordMoment(seat: number, head: number): Promise<void> {
    const kill = () => {
      const cur = this.model.value as MarpeleLocal;
      if (!cur.dead.includes(head)) this.model.value = { ...cur, dead: [...cur.dead, head] };
    };
    if (reducedMotion()) {
      this.fx("sword", head);
      synth("slash");
      kill();
      this.setPower(seat, MAGIC_SWORD, -1);
    } else {
      const T = Math.min(ms(1500), 1900);
      this.mpStrike.value = { seat, head, ms: T, id: ++this.fxId };
      synth("whoosh", { volume: 0.35, delay: T * 0.5 });   // فرودِ تیغه
      await sleep(T * STRIKE_IMPACT);
      this.fx("sword", head);
      synth("slash");
      try { navigator.vibrate?.(30); } catch { /* مرورگرِ بی‌لرزش */ }
      kill();
      await sleep(T * (1 - STRIKE_IMPACT));
      this.mpStrike.value = null;
      this.setPower(seat, MAGIC_SWORD, -1);
    }
    notify(`${this.seatName(seat)} سر مار را زد! این مار دیگر کسی را نیش نمی‌زند`);
    this.offerReaction(seat);
    await wait(300);
  }

  /** لحظه‌ی نیش: سرِ مار حمله می‌کند و هیس، بعد مهره روی بدنش پایین سُر می‌خورد. */
  private async biteMoment(seat: number, head: number, tail: number): Promise<void> {
    this.setCar(seat, 0);   // نیش ماشین را می‌گیرد
    this.fx("bite", head);
    this.mpWalk.value = { seat, step: 0, pose: "scared" };
    synth("hiss", { volume: 0.7 });
    await wait(480);
    this.mpWalk.value = { seat, step: 0, pose: "fall" };
    this.setPos(seat, tail);
    notify(`${this.seatName(seat)} — مار نیشش زد! افتاد پایین`);
    this.offerReaction(seat);
    await wait(900);
    this.mpWalk.value = null;
  }

  private async playMarpele(ev: BoardEvent, m0: MarpeleLocal, seat: number): Promise<void> {
    if (seat < 0 || seat >= m0.n) return;
    switch (ev.kind) {
      case "roll": {
        this.mpPicked.value = 0;
        const dice = [...ev.data];
        this.mpSteps = m0.diceMode === DICE_SUM ? (dice[0] ?? 0) + (dice[1] ?? 0) : m0.diceMode === DICE_CHOOSE ? 0 : dice[0] ?? 0;
        this.model.value = { ...m0, turn: seat, die: dice[0] ?? m0.die, dice, canRoll: false, choose: [], bite: null, mine: false };
        if (reducedMotion()) { await this.rollAnim(); break; }
        {
          // همان پرتابِ منچ: تاسِ من به سوی کشیدنِ انگشت، بقیه رو به وسط؛ دو تاس از کنارِ هم، دومی کمی کج‌تر و کندتر
          const me = !m0.watching;
          const corner = marpeleCorner(seat, m0.n);
          const v = throwVelocity(corner, seat === 0 && me ? this.mnSwipe : null);
          if (seat === 0) this.mnSwipe = null;
          const two = dice.length > 1;
          const turn = (a: number, k: number): [number, number] =>
            [(v[0] * Math.cos(a) - v[1] * Math.sin(a)) * k, (v[0] * Math.sin(a) + v[1] * Math.cos(a)) * k];
          const throws = dice.slice(0, 2).map((value, i) =>
            ({ v: i === 0 ? v : turn(0.3, 0.86), dx: two ? (i === 0 ? -0.8 : 0.8) : 0, value: value > 0 ? value : 6 }));
          const T = Math.min(ms(900), 1200), hold = Math.min(ms(two ? 420 : 260), 600);
          this.mpThrow.value = { seat, corner, throws, ms: T, hold, id: ++this.fxId };
          if (seat === 0 && me) try { navigator.vibrate?.(15); } catch { /* مرورگرِ بی‌لرزش */ }
          await sleep(T + hold);
          this.mpThrow.value = null;
        }
        break;
      }
      case "pick":
        this.mpSteps = ev.a;
        this.mpPicked.value = ev.a;
        await wait(260);
        break;
      case "overshoot":
        notify(`${this.seatName(seat)} سرریز شد — بی‌حرکت`);
        await wait(400);
        break;
      case "move": {
        const from = ev.a, stepped = ev.b, to = ev.c;
        const steps = this.mpSteps > 0 ? this.mpSteps : Math.max(0, stepped - from);
        // سریع: از صد که رد شود برمی‌گردد
        const path: number[] = [];
        for (let i = 0, at = from, dir = 1; i < steps; i++) { if (at === 100) dir = -1; at += dir; path.push(at); }
        if (path[path.length - 1] !== stepped) { path.length = 0; for (let p = from + 1; p <= stepped; p++) path.push(p); }
        const picks = new Map<number, number>();
        for (let i = 0; i + 1 < ev.data.length; i += 2) picks.set(ev.data[i]!, ev.data[i + 1]!);
        const rams = this.mpRams; this.mpRams = new Map();
        // آدمک خانه‌به‌خانه راه می‌رود — همان «تُک» ِ گامِ منچ برای هر خانه، کمی زیرتر با هر گام، کارِ خودم بلندتر
        const mineMove = !m0.watching && seat === 0;
        for (const [k, p] of path.entries()) {
          this.setPos(seat, p);
          this.mpWalk.value = { seat, step: k + 1, pose: "step" };
          sfx("mn_step", { delay: ms(k === 0 && from === 0 ? 180 : 100), volume: mineMove ? 0.6 : 0.38, rate: Math.min(1.35, 0.94 + (k + 1) * 0.035) });
          // سرِ هر خانه: اول کوبیدنِ ماشین، بعد جادوی آن‌جا — همان ترتیبِ move ِ سرور
          const hit = rams.get(p);
          if (hit) { rams.delete(p); await this.ramMoment(seat, p, hit); }
          const kind = picks.get(p);
          if (kind) { picks.delete(p); this.fx("pickup", p, kind); synth("chime", { volume: 0.6 }); }
          if (kind === MAGIC_CAR) this.setCar(seat, CAR_TURNS);
          if (kind === MAGIC_SWORD || kind === MAGIC_ANTIDOTE) this.setPower(seat, kind, 1);
          await wait(k === 0 && from === 0 ? 220 : kind ? 260 : 130);
        }
        for (const [p, hit] of rams) await this.ramMoment(seat, p, hit);   // سرِ راه نیفتاد (نباید): سرِ فرود
        this.mpWalk.value = null;
        for (let i = 1; i < ev.data.length; i += 2) notify(`${this.seatName(seat)} — ${MAGIC_NAME[ev.data[i]!] ?? "جادو"}`);
        this.mpSteps = 0;
        if (to > stepped) {
          await wait(140);
          this.mpWalk.value = { seat, step: 0, pose: "jump" };
          this.setPos(seat, to);
          notify(`${this.seatName(seat)} — پله! رفت بالا`);
          await wait(720);
          this.mpWalk.value = null;
        } else if (to < stepped) {
          await wait(120);
          await this.biteMoment(seat, stepped, to);
        }
        break;
      }
      case "bite":
        await this.biteMoment(seat, ev.a, ev.c);
        break;
      case "carhit": {
        // پیش از move ِ همان حرکت می‌آید؛ کوبیدن سرِ همان خانه، وسطِ راه‌رفتن
        const v = toLocal(ev.a, m0.me, m0.n);
        this.mpRams.set(ev.b, [...(this.mpRams.get(ev.b) ?? []), v]);
        break;
      }
      case "antidote":
        this.fx("antidote", ev.a);
        synth("chime");
        this.setPower(seat, MAGIC_ANTIDOTE, -1);
        notify(`${this.seatName(seat)} پادزهر زد — نیش مار خنثی شد`);
        await wait(900);
        break;
      case "sword":
        await this.swordMoment(seat, ev.a);
        break;
      case "swap": {
        const other = toLocal(ev.a, m0.me, m0.n);
        synth("whoosh");
        const cur = this.model.value as MarpeleLocal;
        const pos = [...cur.pos]; pos[seat] = ev.b; if (other >= 0 && other < cur.n) pos[other] = ev.c;
        this.model.value = { ...cur, pos };
        notify(`${this.seatName(seat)} جایش را با ${this.seatName(other)} عوض کرد!`);
        this.offerReaction(other);
        await wait(900);
        break;
      }
      case "skip":
        notify(`${this.seatName(seat)} روی خانه‌ی ایست رفت — نوبت بعدی‌اش می‌سوزد`);
        await wait(500);
        break;
      case "skipped":
        notify(`نوبت ${this.seatName(seat)} سوخت`);
        await wait(600);
        break;
      case "extra":
        notify(`${this.seatName(seat)} تاس دوباره گرفت!`);
        await wait(500);
        break;
    }
  }
}

/** «حرکتِ کمتر» ِ سیستم — تخته‌نرد بی پرواز، فقط جابه‌جایی و صدا. */
const reducedMotion = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** انتظارِ خام، بی ضریبِ سرعت — برای ضرب‌هایی که طرح به میلی‌ثانیه‌ی مطلق گفته. */
const sleep = (x: number): Promise<void> => new Promise((r) => setTimeout(r, Math.max(0, x)));

/** دوز: خطِ برنده ۱۵۰ میلی‌ثانیه بعد ۴۵۰ میلی‌ثانیه کشیده می‌شود (‎.dz-strike) و بعد بیش از یک ثانیه تنها روی تخته می‌مانَد. */
export const DOOZ_LINE_HOLD = 1800;

/** رتبه به حروف — «نفرِ دوم شدی». */
export const menchOrdinal = (k: number): string => ["اول", "دوم", "سوم", "چهارم"][k - 1] ?? String(k);

/** نامِ خانه‌های جادویی برای پیام — همان MAGIC_* ِ state. */
const MAGIC_NAME: Record<number, string> = {
  1: "پادزهر گرفت", 2: "شمشیر گرفت", 3: "تاس دوباره گرفت", 4: "جابه‌جایی با نفر اول", 5: "خانه‌ی ایست",
  6: "سوار ماشین شد؛ سه نوبت هر کس سر راهش باشد برمی‌گردد سر شروع",
};

/** ورودیِ مدل را ببند — جوابِ حرکتِ ما هنوز نرسیده. */
function closeInput(m: BoardLocal): void {
  m.mine = false;
  switch (m.game) {
    case "mench": m.canRoll = false; m.tokens = []; m.hints = []; break;
    case "dooz": m.cols = []; break;
    case "nard": m.canRoll = false; m.moves = []; break;
    case "domino": m.plays = []; m.canDraw = false; break;
    case "marpele": m.canRoll = false; m.choose = []; m.bite = null; break;
  }
}

/** کلیدِ «همان نما» — نمایی که حرکت از رویش فرستاده شد. */
function posKey(st: BoardTableState): string {
  return `${st.turn}|${st.phase}|${st.round}|${st.board.join(",")}|${st.dice.join(",")}|${st.hand.join(",")}|${st.over}`;
}

export type { BoardLocal };
